// src/encounter/services/use-save-condition.ts
// Rail-scoped save hook for the IPS Active/Resolved Problems sections. Wraps the curl-verified
// Condition endpoints (POST /api/Patients/CreateCondition, PUT /api/Patients/condition/{id})
// and invalidates the ENCOUNTER summary query (['patient-summary', patientId]) so the
// pre-encounter rail reflects the change — intentionally NOT the patients-module chart key
// (['patient-conditions', patientId]). Mirrors use-save-allergy / use-save-flags: transport +
// cache only; body assembly + wire-shape rules live in lib/to-condition-request.ts.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { ConditionRequest } from '@/encounter/lib/to-condition-request';

export function useSaveCondition(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, ConditionRequest>({
    mutationFn: (req: ConditionRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateCondition', req.body);
      }
      // "Entered in error" (left-rail target §3): the Scrips.Patient T4 transition, with its required reason.
      if (req.kind === 'entered-in-error') {
        return post<string>(`/api/Patients/condition/${req.conditionId}/entered-in-error`, req.body);
      }
      // update path covers EDIT, active↔resolved transition, and soft-DELETE.
      return put<string>(`/api/Patients/condition/${req.conditionId}`, req.body);
    },
    onSuccess: () => {
      // Invalidate the summary (rail render source) AND the full chart lists the rail sources
      // edit/delete from (usePatientConditions, ['patient-conditions', patientId, status] — the
      // prefix match covers both Active + Resolved). Without this a freshly-added/edited row's full
      // record stays stale and its affordance can't complete the round-trip (Codex MAJ-2).
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-conditions', patientId] });
    },
  });
}
